import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

// Error message display duration - gives users time to read the message.
// Mirrors assets/product-form.js's ERROR_MESSAGE_DISPLAY_DURATION.
const ERROR_MESSAGE_DISPLAY_DURATION = 10000;

// Button re-enable delay after error - prevents rapid repeat attempts.
// Mirrors assets/product-form.js's ERROR_BUTTON_REENABLE_DELAY.
const ERROR_BUTTON_REENABLE_DELAY = 1000;

class RoutineBundleComponent extends Component {
  requiredRefs = ['checkboxes', 'totalDisplay', 'addButton', 'liveRegion'];

  /** @type {number | undefined} */
  #errorMessageTimeout;

  connectedCallback() {
    super.connectedCallback();
    // The initial markup renders the base product's price as a static
    // placeholder (Liquid can't know which checkboxes are checked at
    // render time). Compute the real sum of all checked items as soon as
    // refs are available, rather than waiting for the first checkbox
    // `change` event.
    this.updateTotal();
  }

  /**
   * Recompute and display the running total from checked items, replicating
   * the `money` Liquid filter client-side (same pattern as assets/facets.js),
   * using the `data-money-format` / `data-currency` attributes rendered by
   * the block. `{{ variant.price }}` (no `money` filter) renders the raw
   * Money-typed value as minor units (cents) already — e.g. `9900` for
   * $99.00 — so `data-price` is already in cents and must NOT be multiplied
   * by 100 again here. `totalDisplay` carries `aria-live="polite"`, so
   * updating its text also announces the new total to screen readers.
   */
  updateTotal() {
    const currency = this.dataset.currency || 'USD';
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';

    const totalCents = this.refs.checkboxes
      .filter((box) => /** @type {HTMLInputElement} */ (box).checked)
      .reduce((sum, box) => sum + Number(/** @type {HTMLElement} */ (box).dataset.price || 0), 0);

    this.refs.totalDisplay.textContent = formatMoney(totalCents, moneyFormat, currency);
  }

  /**
   * The primary product's checkbox is intentionally always-checked (it's not
   * optional — it's the product whose page this is). It uses `aria-disabled`
   * rather than the native `disabled` attribute so it stays perceivable and
   * in the tab order for assistive tech (native `disabled` is pulled from
   * the accessibility tree/tab order inconsistently across screen readers),
   * while still being functionally locked: canceling the `click` event
   * prevents the native checked-state toggle for both pointer and keyboard
   * (Space) activation.
   * @param {Event} event
   */
  preventLockedToggle(event) {
    event.preventDefault();
  }

  /**
   * Fetches the current cart from the Cart API. Mirrors the fallback branch of
   * assets/product-form.js's `#refreshCart()` — the full cart JSON (not the
   * `/cart/add.js` response, which for a multi-item add only echoes back the
   * added line items, not cart-wide totals).
   */
  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  /**
   * Shows an inline error message near the "Add Routine to Bag" button and
   * announces it via the live region, auto-hiding after
   * ERROR_MESSAGE_DISPLAY_DURATION. Mirrors assets/product-form.js's
   * addToCartTextError / #setLiveRegionText pattern.
   * @param {string} message
   */
  #showError(message) {
    if (this.#errorMessageTimeout) clearTimeout(this.#errorMessageTimeout);

    const { addToCartTextError } = this.refs;
    if (addToCartTextError) {
      addToCartTextError.textContent = message;
      addToCartTextError.classList.remove('hidden');

      this.#errorMessageTimeout = window.setTimeout(() => {
        addToCartTextError.classList.add('hidden');
        this.#clearLiveRegionText();
      }, ERROR_MESSAGE_DISPLAY_DURATION);
    }

    this.#setLiveRegionText(message);
  }

  /** Hides the inline error message, e.g. on a subsequent successful add. */
  #hideError() {
    if (this.#errorMessageTimeout) clearTimeout(this.#errorMessageTimeout);

    const { addToCartTextError } = this.refs;
    if (addToCartTextError) addToCartTextError.classList.add('hidden');
  }

  /** @param {string} text */
  #setLiveRegionText(text) {
    this.refs.liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    this.refs.liveRegion.textContent = '';
  }

  /**
   * Add every checked, available line item to the cart in one request, then
   * let the theme's own cart-drawer event bus take over (open drawer,
   * refresh totals). Mirrors the dispatch shape used by
   * assets/product-form.js:410-423, including converting the AJAX cart
   * response via CartLinesUpdateEvent.createCartFromAjaxResponse before
   * resolving — the raw `/cart/add.js` response is not in the shape
   * (`totalQuantity`, `cost.totalAmount`, etc.) listeners expect.
   *
   * Guards against double-submit (disables `addButton` for the duration of
   * the request, bailing early if already disabled/in-flight) and surfaces
   * failures via CartErrorEvent + inline text + the live region, mirroring
   * assets/product-form.js's error-handling path — a shopper should never
   * see nothing happen with no explanation.
   */
  async addAllToCart() {
    const { addButton } = this.refs;

    // Already in-flight from a previous click — ignore rapid repeat clicks.
    if (addButton.disabled) return;

    // Out-of-stock routine items are rendered `disabled` and unchecked by
    // default (see the block file), so `.checked` naturally excludes them
    // here without any extra filtering.
    const lines = this.refs.checkboxes
      .filter((box) => /** @type {HTMLInputElement} */ (box).checked)
      .map((box) => ({
        id: Number(/** @type {HTMLElement} */ (box).dataset.variantId),
        quantity: 1,
      }));

    if (lines.length === 0) return;

    addButton.disabled = true;

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: lines.map((line) => ({ merchandiseId: String(line.id), quantity: line.quantity })),
        promise: deferredEventPromise.promise,
      })
    );

    let didError = false;

    try {
      const response = await fetch(
        Theme.routes.cart_add_url,
        fetchConfig('json', { body: JSON.stringify({ items: lines }) })
      );
      const result = await response.json();

      // Shopify's Cart API returns a `status` field (e.g. 422) in the JSON body on error.
      if (!response.ok || result.status) {
        didError = true;

        const errorMessage = result.message || 'Add Routine to Bag failed';

        this.dispatchEvent(
          new CartErrorEvent({
            error: errorMessage,
            code: 'INVALID',
            detail: {
              description: result.description,
              errors: result.errors,
            },
          })
        );

        this.#showError(errorMessage);
      } else {
        this.#hideError();
      }
    } catch (error) {
      didError = true;

      const errorMessage = 'Add Routine to Bag failed';

      this.dispatchEvent(
        new CartErrorEvent({
          error: errorMessage,
          code: 'INVALID',
        })
      );

      this.#showError(errorMessage);
    }

    try {
      const cart = await this.#fetchCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { didError },
      });
    } catch (error) {
      deferredEventPromise.reject(error);
    } finally {
      if (didError) {
        // Brief delay before re-enabling on error, mirroring
        // assets/product-form.js's ERROR_BUTTON_REENABLE_DELAY — prevents
        // a shopper from immediately re-triggering the same failure.
        setTimeout(() => {
          addButton.disabled = false;
        }, ERROR_BUTTON_REENABLE_DELAY);
      } else {
        addButton.disabled = false;
      }
    }
  }
}

if (!customElements.get('routine-bundle')) {
  customElements.define('routine-bundle', RoutineBundleComponent);
}
